// THE THREE EMPTY STATES (UNIFIED-FUNCTION F5.5; clients N8 is the canonical spec).
//
// A zone with no rows is telling the operator one of three DIFFERENT things, and collapsing
// them into one grey "No data" is the single most common way this product could lie:
//
//   error     the fetch failed. We do not know what is there. Name the endpoint, state
//             explicitly that NOTHING WAS WRITTEN, offer the retry.
//   no-match  the fetch succeeded; the filter excluded everything. Name the filter, offer to
//             clear it. There ARE rows — just not these.
//   empty     the fetch succeeded and there is genuinely nothing. State the schema fact:
//             what would have to happen for a row to exist.
//
// B8 in prose form: a missing check never renders as a passed check. `error` is not `empty`.
//
// DRESS (operator's design law): the state sits on a white sheet against the warm field, and
// its control lives inside that sheet. Nothing here floats on the bare field.

import { Fragment, type ReactNode } from 'react';
import type { EmptyKind } from '../../shared/problems.js';
import { useT } from '../i18n/index.js';
import './EmptyState.de.js';
import './EmptyState.fr.js';
import '../i18n/problems.de.js';
import '../i18n/problems.fr.js';

export interface EmptyStateProps {
  kind: EmptyKind;
  /** The headline. One line, sentence case, no terminal period. */
  title: string;
  /**
   * The body. For `error` this must be the SERVER'S OWN message — never a paraphrase (B7).
   * For `no-match` it names the filter that is doing the excluding. For `empty` it states the
   * schema fact.
   */
  detail: ReactNode;
  /** `error` only: re-run the fetch that failed. */
  onRetry?: () => void;
  /** `no-match` only: drop the filter and show what is actually there. */
  onClear?: () => void;
  /** `no-match` only: the label of the control, e.g. 'Clear the status filter'. */
  clearLabel?: string;
  /** Any other control the zone wants inside the sheet (e.g. 'Upload the first document'). */
  children?: ReactNode;
  className?: string;
}

const TITLE_PREFIX: Record<EmptyKind, string> = {
  error: 'Could not load',
  'no-match': 'Nothing matches',
  empty: 'Nothing here yet',
};

export default function EmptyState({
  kind, title, detail, onRetry, onClear, clearLabel, children, className,
}: EmptyStateProps) {
  const t = useT();
  return (
    <div className={`empty-state-card ${className ?? ''}`.trim()} data-empty-kind={kind}>
      <div className="empty-state">
        <div className="empty-state-title">{title || t(TITLE_PREFIX[kind])}</div>
        <div className="empty-state-desc">{detail}</div>

        {kind === 'error' && (
          <div className="empty-state-desc mt-8">
            {/* The one sentence that makes an error survivable: it says the world did not move. */}
            <em>{t('Nothing was written.')}</em>
          </div>
        )}

        {(onRetry || onClear || children) && (
          <div className="mt-16 flex items-center justify-center gap-8">
            {kind === 'error' && onRetry && (
              <button type="button" className="btn btn-sm" onClick={onRetry}>{t('Try again')}</button>
            )}
            {kind === 'no-match' && onClear && (
              <button type="button" className="btn btn-sm" onClick={onClear}>
                {clearLabel ?? t('Clear the filter')}
              </button>
            )}
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

/** A translated sentence whose `{placeholders}` are React nodes (a `<code>`, a styled ref) — the
 *  sentence is translated with its placeholders intact, then split around them. */
function nodes(text: string, parts: Record<string, ReactNode>): ReactNode[] {
  return text.split(/(\{\w+\})/).map((seg, i) => {
    const m = /^\{(\w+)\}$/.exec(seg);
    return m && m[1] in parts ? <Fragment key={i}>{parts[m[1]]}</Fragment> : seg;
  });
}

/**
 * The degraded-zone shorthand: given the composite payload's `problems[]` and the source this
 * zone reads, render the error state with the producer's own words — or render nothing and let
 * the zone paint its data.
 */
export function ProblemState({
  problems, source, zone, onRetry,
}: {
  problems: { source: string; message: string }[];
  source: string;
  /** What the operator loses, in their words: 'the gate tallies', 'the sidebar counts'. */
  zone: string;
  onRetry?: () => void;
}) {
  const t = useT();
  const hit = problems.find((p) => p.source === source);
  if (!hit) return null;
  return (
    <EmptyState
      kind="error"
      title={t('{zone} could not be computed', { zone })}
      detail={<>{nodes(t('The {source} source answered: “{message}”'), { source: <code>{source}</code>, message: hit.message })}</>}
      onRetry={onRetry}
    />
  );
}
